import { getFunctions, httpsCallable } from "firebase/functions";
import { CapacitorHttp } from "@capacitor/core";
import { getBestEffortIdToken } from "./nativeAuthBridge";
import { shouldUseNativeDatabase } from "./firebase-native-database";

const FUNCTIONS_REGION = import.meta.env.VITE_FIREBASE_functionsRegion || "europe-west1";
const FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_projectId || "";

export const DEFAULT_CALLABLE_TIMEOUT_MS = 25000;

/**
 * Error thrown by callable invocations. `code` mirrors the Firebase Functions
 * error status (e.g. "resource-exhausted", "unavailable") so callers can map it
 * to user-facing messages.
 */
export class CallableError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "CallableError";
    this.code = code;
  }
}

/**
 * Races a promise against a timeout so a hung request rejects instead of
 * spinning forever. iOS WKWebView requests can stall silently, so every
 * callable path is bounded.
 */
export function withCallableTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string
): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new CallableError("unavailable", timeoutMessage));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

/**
 * Invokes a Firebase callable over the native HTTP stack (CapacitorHttp).
 *
 * On iOS the app is served from a custom URL-scheme handler, which breaks the
 * Firebase Web SDK's fetch/XHR transport (requests hang until they time out).
 * Posting directly to the callable endpoint through the native network stack
 * bypasses the webview entirely, matching the callable wire protocol
 * (`{ data }` request, `{ result }` / `{ error }` response).
 */
async function invokeCallableViaNativeHttp<TRequest, TResponse>(
  functionName: string,
  data: TRequest,
  timeoutMs: number
): Promise<TResponse> {
  if (!FIREBASE_PROJECT_ID) {
    throw new CallableError("failed-precondition", "Firebase project is not configured");
  }

  const url = `https://${FUNCTIONS_REGION}-${FIREBASE_PROJECT_ID}.cloudfunctions.net/${functionName}`;
  const token = await getBestEffortIdToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await CapacitorHttp.post({
    url,
    headers,
    data: { data },
    connectTimeout: timeoutMs,
    readTimeout: timeoutMs,
  });

  const payload = typeof response.data === "string"
    ? JSON.parse(response.data)
    : response.data;

  if (payload?.error) {
    const status = String(payload.error.status || "internal").toLowerCase();
    const message = payload.error.message || "Callable request failed";
    throw new CallableError(status, message);
  }

  if (!payload || typeof payload !== "object" || !("result" in payload)) {
    throw new CallableError("internal", "Unexpected callable response format");
  }

  return payload.result as TResponse;
}

export interface CallFirebaseFunctionOptions {
  timeoutMs?: number;
  timeoutMessage?: string;
}

/**
 * Calls a Firebase callable function, transparently using the native HTTP
 * transport on iOS/Capacitor and the Firebase Web SDK on web/Android.
 *
 * Both paths are bounded by a timeout so requests never hang indefinitely.
 */
export async function callFirebaseFunction<TRequest, TResponse>(
  functionName: string,
  data: TRequest,
  options: CallFirebaseFunctionOptions = {}
): Promise<TResponse> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_CALLABLE_TIMEOUT_MS;
  const timeoutMessage = options.timeoutMessage ?? "Request timed out. Please retry shortly.";
  const usesNativeHttp = shouldUseNativeDatabase();

  if (usesNativeHttp) {
    return withCallableTimeout(
      invokeCallableViaNativeHttp<TRequest, TResponse>(functionName, data, timeoutMs),
      timeoutMs,
      timeoutMessage
    );
  }

  const functions = getFunctions(undefined, FUNCTIONS_REGION);
  const callable = httpsCallable<TRequest, TResponse>(functions, functionName);
  const result = await withCallableTimeout(callable(data), timeoutMs, timeoutMessage);
  return result.data as TResponse;
}
